import React, { Component } from "react";
import ReactDOM from "react-dom";
import { Marked, Renderer } from "marked";
import { markedHighlight } from "marked-highlight";
import hljs from "highlight.js/lib/common";
import DOMPurify from "dompurify";

import Theme from "context/theme";
import CopyButton from "./copyButton";
import DiagramControls from "./diagramControls";
import { attachViewer } from "./diagramViewer";
import { mermaidConfig } from "./mermaidTheme";
// highlight.js ships NO themeable stylesheet: github.css and github-dark.css both declare bare,
// unscoped `.hljs-keyword {}` rules, so importing both leaves whichever webpack emits last winning
// globally with no selector to toggle. The 15 rules are restated once against the --bh-hl-* family
// in src/vendor.theme.css instead - light is github.css value for value, dark is github-dark.css.
import "./markdown.css";

// Shared Marked instance with syntax highlighting. Used by every widget
// that renders user-authored markdown so the visual treatment is the
// same everywhere (thread widget, message widget, …).
const playgroundMarked = new Marked(
  markedHighlight({
    langPrefix: "hljs language-",
    highlight(code, lang) {
      // A mermaid fence is a DIAGRAM, not source: it must not be highlighted, and must not be
      // HTML-escaped either, since the definition is handed back to mermaid verbatim below.
      if ( "mermaid" === lang ) {
        return code;
      }
      const language = lang && hljs.getLanguage(lang) ? lang : "plaintext";
      try {
        return hljs.highlight(code, { language, ignoreIllegals: true }).value;
      } catch (e) {
        return code;
      }
    }
  })
);

// Audio and video are written as a markdown image, told apart by the extension of the URL's path:
// docs/specs/2026-10-18-readme-markdown.md#3--markdownbody-embeds-audio-and-video
const MEDIA_EXTENSIONS = {
  video: [".mp4", ".webm", ".mov", ".ogv"],
  audio: [".mp3", ".wav", ".ogg", ".oga", ".m4a", ".flac"],
};

/** "video" or "audio" for a URL whose path ends in one of that kind's extensions, else null. */
function mediaKind(href) {
  let path;
  try {
    path = new URL(href, "https://markdown.invalid/").pathname.toLowerCase();
  } catch (e) {
    return null;
  }
  const kind = Object.keys(MEDIA_EXTENSIONS)
    .find(name => MEDIA_EXTENSIONS[name].some(extension => path.endsWith(extension)));
  return kind || null;
}

/**
 * A player for a media image, or false to leave it to marked's own <img>.
 *
 * @param token marked's image token, whose `text` the lexer has already HTML-escaped
 */
function renderMedia(token) {
  const kind = mediaKind(token.href);
  if ( !kind ) {
    return false;
  }
  let src;
  try {
    src = encodeURI(token.href).replace(/%25/g, "%");
  } catch (e) {
    return token.text;
  }
  // A span, not a figure: marked wraps an image in a <p>, which a block element would close early.
  const label = token.text ? ` aria-label="${token.text}"` : "";
  const width = token.width ? ` data-width="${token.width}"` : "";
  const caption = token.text ? `<span class="playground-media-caption">${token.text}</span>` : "";
  return `<span class="playground-media"><${kind} controls preload="metadata" src="${src}"${label}${width}>`
    + `</${kind}>${caption}</span>`;
}

// `{width=60%}` or `{width=480}` touching an image: Pandoc's attribute syntax, of which only the width is read.
// docs/specs/2026-10-18-readme-markdown.md#5--pictures-scale-and-sit-centered
const IMAGE_WIDTH = /^\{\s*width\s*=\s*(\d+(?:\.\d+)?)(%|px)?\s*\}/;
const WIDTH_VALUE = /^\d+(?:\.\d+)?(?:%|px)$/;

/** Moves a width suffix off the text that follows an image and onto the image token. */
function takeImageWidths(tokens) {
  tokens.forEach((token, index) => {
    const next = tokens[index + 1];
    if ( "image" !== token.type || !next || "text" !== next.type ) {
      return;
    }
    const match = IMAGE_WIDTH.exec(next.raw);
    if ( match ) {
      token.width = match[1] + (match[2] || "px");
      next.raw = next.raw.slice(match[0].length);
      next.text = next.text.slice(match[0].length);
    }
  });
}

/** The image a paragraph holds and nothing else, linked or not; null when it holds anything more. */
function standaloneImage(tokens) {
  const content = tokens.filter(token => !("text" === token.type && !token.raw.trim()));
  if ( 1 !== content.length ) {
    return null;
  }
  const [only] = content;
  if ( "link" === only.type ) {
    return standaloneImage(only.tokens || []);
  }
  return "image" === only.type ? only : null;
}

/** marked's own <img>, carrying the width for MarkdownBody to apply once the HTML is sanitized. */
function renderImage(token) {
  const media = renderMedia(token);
  if ( false !== media || !token.width ) {
    return media;
  }
  return Renderer.prototype.image.call(this, token).replace(/^<img /, `<img data-width="${token.width}" `);
}

/** A paragraph holding one picture, video or audio clip becomes a centered figure; a picture's title is its caption. */
function renderParagraph(token) {
  const image = standaloneImage(token.tokens);
  if ( !image ) {
    return false;
  }
  const caption = image.title && !mediaKind(image.href)
    ? `<span class="playground-figure-caption">${image.title}</span>` : "";
  return `<p class="playground-figure">${this.parser.parseInline(token.tokens)}${caption}</p>\n`;
}

/**
 * Puts each code block in a frame beside a host for its copy button, outside the <pre> so the button stays put while
 * the block scrolls sideways. A block keeps a frame this body made; markup wearing the class is not one. Mermaid fences
 * are diagrams and get none.
 *
 * @param frames each frame this body made, to its copy button's host
 * @return one { kind: "code", host, text } per block
 */
function frameCodeBlocks(root, frames) {
  return Array.from(root.querySelectorAll("pre > code"))
    .filter(code => !code.closest(".playground-mermaid"))
    .map(code => {
      const pre = code.parentElement;
      if ( !frames.has(pre.parentElement) ) {
        const frame = document.createElement("div");
        frame.className = "playground-code";
        pre.replaceWith(frame);
        const host = document.createElement("span");
        host.className = "playground-code-copy";
        frame.append(pre, host);
        frames.set(frame, host);
      }
      // marked ends every block with a newline the author did not write.
      return { kind: "code", host: frames.get(pre.parentElement), text: code.textContent.replace(/\n$/, "") };
    });
}

/** A diagram's definition, decoded from `data-mermaid`; "" when it does not decode. */
function decodeDefinition(node) {
  try {
    return decodeURIComponent(node.getAttribute("data-mermaid") || "");
  } catch (e) {
    return "";
  }
}

/**
 * Gives each diagram a canvas to be drawn into, so a redraw never touches its controls, and a host for the controls,
 * which markdown.css shows once it is drawn. A diagram keeps the parts this body made for it.
 *
 * @param parts each diagram this body has framed, to its { canvas, host }
 * @return one { kind: "diagram", host, node, source } per diagram
 */
function frameDiagrams(root, parts) {
  return Array.from(root.querySelectorAll(".playground-mermaid[data-mermaid]"), node => {
    if ( !parts.has(node) ) {
      // The renderer's placeholder is empty, so whatever a diagram holds before it is framed, an author wrote.
      node.textContent = "";
      const canvas = document.createElement("div");
      canvas.className = "playground-mermaid-canvas";
      const host = document.createElement("div");
      host.className = "playground-mermaid-controls";
      host.setAttribute("role", "group");
      host.setAttribute("aria-label", "Diagram controls");
      node.append(canvas, host);
      parts.set(node, { canvas, host });
    }
    return { kind: "diagram", host: parts.get(node).host, node, source: decodeDefinition(node) };
  });
}

/** Sets each validated `data-width` as the element's width; the sanitizer forbids `style`, so it happens after. */
function applyWidths(root) {
  for ( const element of root.querySelectorAll("img[data-width], video[data-width], audio[data-width]") ) {
    const width = element.getAttribute("data-width");
    if ( WIDTH_VALUE.test(width) ) {
      element.style.width = width;
    }
  }
}

// A ```mermaid fence becomes a placeholder that MarkdownBody fills in after mount. It cannot be
// rendered here: mermaid's API is asynchronous and measures text against a live element. The
// definition rides in a `data-` attribute (DOMPurify keeps those by default) rather than in the
// element's text, so the page never flashes a wall of arrows before the diagram lands.
playgroundMarked.use({
  renderer: {
    code(token) {
      if ( "mermaid" !== token.lang ) {
        return false; // fall through to the highlighter above
      }
      let definition;
      try {
        definition = encodeURIComponent(token.text);
      } catch (e) {
        return false; // a lone surrogate: the fence is shown as its source, escaped, as any other fence is
      }
      return `<div class="playground-mermaid" data-mermaid="${definition}"></div>`;
    },
    image: renderImage,
    paragraph: renderParagraph
  },
  walkTokens(token) {
    if ( "table" === token.type ) {
      [token.header, ...token.rows].flat().forEach(cell => takeImageWidths(cell.tokens));
    } else if ( Array.isArray(token.tokens) ) {
      takeImageWidths(token.tokens);
    }
  }
});

// Mermaid is loaded on demand: it is a large dependency and most markdown carries no diagram, so
// the cost is paid by the first view that actually shows one.
//
// WHAT IS CACHED IS THE MODULE, NOT THE INITIALISATION. `mermaid.initialize` is a module-global side
// effect and `mermaid.render` bakes the palette into the SVG string it returns, so the only way to
// change the palette is to call initialize again — which renderDiagrams does before every pass. It
// is idempotent and cheap. Initialising once here, as this file used to, meant the first diagram on
// the page chose the palette for every diagram after it, for the life of the tab.
let mermaidPromise = null;
function loadMermaid() {
  if ( !mermaidPromise ) {
    mermaidPromise = import("mermaid").then(({ default: mermaid }) => mermaid);
  }
  return mermaidPromise;
}

let mermaidSeq = 0;

// DOMPurify's defaults stop scripts, not markup that restyles the whole page or posts a form off-site,
// and a README is public. `input` stays: a task list is `<input disabled type="checkbox">`.
const SANITIZE_OPTIONS = {
  FORBID_TAGS: ["style", "form", "button", "textarea", "select"],
  FORBID_ATTR: ["style"],
};

// The class names the renderers feeding sanitizeHtml emit: highlight.js's (`hljs`, `hljs-*`, sub-scopes such as
// `class_` and `inherited__`), `language-*`, this file's own, and the debugger's table. Any other name is an author's,
// and Bootstrap's utilities alone (`fixed-top vh-100 w-100`) rebuild the overlay a `style=` would have drawn.
const RENDERED_CLASSES = new Set(["hljs", "table", "table-hover", "playground-mermaid", "playground-media",
  "playground-media-caption", "playground-figure", "playground-figure-caption"]);
const RENDERED_CLASS_PATTERN = /^(?:hljs-[\w-]+|[a-z]+_+|language-\S+)$/;

// Its own instance, so the class hook reaches no other DOMPurify caller.
const purifier = DOMPurify(window);
purifier.addHook("uponSanitizeAttribute", (node, data) => {
  if ( "class" === data.attrName ) {
    data.attrValue = data.attrValue.split(/\s+/)
      .filter(name => RENDERED_CLASSES.has(name) || RENDERED_CLASS_PATTERN.test(name)).join(" ");
    data.keepAttr = "" !== data.attrValue;
  }
});

/** Markdown-rendered HTML made safe to hand to `dangerouslySetInnerHTML`. */
export function sanitizeHtml(html) {
  return purifier.sanitize(html, SANITIZE_OPTIONS);
}

/**
 * MarkdownBody — the one markdown renderer, now with mermaid.
 *
 * A diagram that fails to parse falls back to its own source in a `<pre>`, not to an empty box: a
 * broken diagram is usually one somebody needs to read and fix, and hiding the text hides the fix.
 *
 * Props:
 *   text      — the markdown source
 *   className — added to `.playground-markdown`
 *   breaks    — treat a single newline as a line break (see below)
 *
 * **`breaks` is for text somebody TYPED**, and it is off by default because the two kinds of
 * markdown here want opposite things. A document — an `.md` artifact, a plan, a report — is written
 * with hard-wrapped paragraphs, and honouring every newline in one shreds it into ragged lines. A
 * message typed into a field is the other way round: Enter sends and Shift+Enter is a newline, so
 * every newline in it was somebody deliberately starting a new line, and joining two of them into a
 * paragraph loses what they wrote. GitHub draws the same line between a comment box and a file.
 */
export class MarkdownBody extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.cancelled = false;
    // The controls MarkdownBody adds to its sanitized DOM, rendered through portals into hosts decorate() made.
    this.state = { chrome: [] };
    // Each drawn diagram's viewer, by its `.playground-mermaid` node; a redraw replaces it.
    this.viewers = new WeakMap();
    // What decorate() made, so it reuses its own frames and never markup an author dressed in the same classes.
    this.codeFrames = new WeakMap();
    this.diagramParts = new WeakMap();
    this.lastParse = null;
  }

  componentDidMount() {
    this.decorate();
    this.renderDiagrams();
    // A theme change re-renders every diagram in this body. mermaid bakes its palette into the SVG
    // string, so this is a re-render and not a repaint — no stylesheet can reach a drawn diagram.
    // One subscription per mounted body, and there is one body per widget, so this scales with what
    // is actually on screen.
    this.unsubscribeTheme = Theme.subscribe(() => this.renderDiagrams(true));
  }

  componentDidUpdate(previous) {
    if ( previous.text !== this.props.text || !!previous.breaks !== !!this.props.breaks ) {
      this.decorate();
      this.renderDiagrams();
    }
  }

  /** What the sanitizer must not see, added after it: widths, code blocks' copy buttons, diagrams' controls. */
  decorate() {
    const root = this.ref.current;
    const chrome = root ? [...frameCodeBlocks(root, this.codeFrames), ...frameDiagrams(root, this.diagramParts)] : [];
    if ( root ) {
      applyWidths(root);
    }
    if ( chrome.length || this.state.chrome.length ) {
      this.setState({ chrome });
    }
  }

  renderChrome(item) {
    if ( "diagram" === item.kind ) {
      const viewer = () => this.viewers.get(item.node) || null;
      return <DiagramControls host={item.node} source={item.source} viewer={viewer} />;
    }
    return <CopyButton text={item.text} label="Copy code" />;
  }

  componentWillUnmount() {
    this.cancelled = true;
    if ( this.unsubscribeTheme ) this.unsubscribeTheme();
  }

  /**
   * `force` re-draws diagrams that are ALREADY drawn — the theme-change path. Without it only
   * never-drawn placeholders are picked up, which is what the mount and update paths want.
   *
   * The definition now stays in `data-mermaid` for the life of the node. It used to be removed the
   * moment the diagram was drawn, which meant a theme change had nothing left to re-render FROM;
   * `data-mermaid-drawn` is the flag that used to be "the attribute is gone". Keeping it costs one
   * encoded copy of the source per diagram and is what makes the switch possible at all.
   */
  async renderDiagrams(force) {
    const root = this.ref.current;
    if ( !root ) return;
    const selector = force
      ? ".playground-mermaid[data-mermaid]"
      : ".playground-mermaid[data-mermaid]:not([data-mermaid-drawn])";
    const nodes = Array.from(root.querySelectorAll(selector));
    if ( 0 === nodes.length ) return;

    let mermaid;
    try {
      mermaid = await loadMermaid();
      // Re-initialised on every pass, because the palette is a module global inside mermaid and this
      // is the only way to change it. It must happen before the renders below, not once at load.
      mermaid.initialize(mermaidConfig(Theme.resolved()));
    } catch (e) {
      return; // unavailable — the placeholders stay empty rather than taking the page down
    }
    if ( this.cancelled ) return;

    for ( const node of nodes.filter(node => this.diagramParts.has(node)) ) {
      const definition = decodeDefinition(node);
      const { canvas } = this.diagramParts.get(node);
      node.classList.remove("playground-mermaid-failed");
      try {
        const { svg } = await mermaid.render(`bh-mermaid-${++mermaidSeq}`, definition);
        if ( this.cancelled ) return;
        canvas.innerHTML = svg;
        node.setAttribute("data-mermaid-drawn", "1");
        // A new drawing gets a new viewer, so a redraw starts from the view mermaid fitted.
        this.viewers.set(node, attachViewer(node));
      } catch (e) {
        if ( this.cancelled ) return;
        this.viewers.delete(node);
        node.classList.add("playground-mermaid-failed");
        node.setAttribute("data-mermaid-drawn", "1");
        const pre = document.createElement("pre");
        pre.textContent = definition;
        canvas.innerHTML = "";
        canvas.appendChild(pre);
      }
    }
  }

  /** The sanitized HTML for one (text, breaks), parsed once: decorate() re-renders after every mount. */
  parsed(text, breaks) {
    if ( !this.lastParse || this.lastParse.text !== text || this.lastParse.breaks !== breaks ) {
      // Per call, not per instance: the same renderer serves documents and typed messages, and the
      // option does not leak from one parse to the next.
      this.lastParse = { text, breaks, html: { __html: sanitizeHtml(playgroundMarked.parse(text, { breaks })) } };
    }
    return this.lastParse.html;
  }

  render() {
    const { text, className, breaks } = this.props;
    if ( typeof text !== "string" ) {
      return null;
    }
    const cls = className ? `playground-markdown ${className}` : "playground-markdown";
    const html = this.parsed(text, !!breaks);
    // React leaves the hosts alone: it compares `__html` and rewrites the DOM only when the HTML itself changes.
    // A portal goes only into this body's own element: anything else throws, and the app has no error boundary.
    const root = this.ref.current;
    return (
      <>
        <div ref={this.ref} className={cls} dangerouslySetInnerHTML={html} />
        {this.state.chrome.map((item, index) => item.host instanceof Element && root && root.contains(item.host)
          ? ReactDOM.createPortal(this.renderChrome(item), item.host, `chrome-${index}`)
          : null)}
      </>
    );
  }
}

export default MarkdownBody;
